/**
 * Everything the world is made of, searched as one list.
 *
 * Apart from the panel that draws it, because it is a question about the
 * definitions rather than a piece of interface, and because a file that exports
 * both cannot be swapped in while the game is running.
 */

import type {
  ItemDefinitionWire,
  ModelWire,
  NpcDefinitionWire,
  ObjectDefinitionWire,
  QuestWire,
} from '@inverse/protocol';

/**
 * Something a panel has been asked to open, from outside itself.
 *
 * The count is the point: asking for the same thing twice is two requests, and
 * a panel that only watched the id would ignore the second one.
 */
export interface Wanted {
  id: string;
  at: number;
}

/** What a hit is, and where pressing it goes. */
export interface Found {
  kind: 'item' | 'character' | 'scenery' | 'quest' | 'model' | 'place' | 'shop';
  /** The number or name the world knows it by. */
  id: string;
  name: string;
  /** A word or two saying what it is, for a list of mixed things. */
  note: string;
}

export const KIND_LABELS: Readonly<Record<Found['kind'], string>> = {
  item: 'Item',
  character: 'Character',
  scenery: 'Scenery',
  quest: 'Quest',
  model: 'Model',
  place: 'Place',
  shop: 'Shop',
};

/**
 * Everything matching what was typed, in the order somebody means it.
 *
 * A number is almost always an id, so an exact id comes first. After that,
 * whatever starts with the words typed, then whatever merely contains them:
 * typing "cop" should offer the Copper Bar before the Smithing Hammer that
 * happens to mention copper.
 */
export function findIn(
  wanted: string,
  world: {
    items: readonly ItemDefinitionWire[];
    characters: readonly NpcDefinitionWire[];
    objects: readonly ObjectDefinitionWire[];
    quests: readonly QuestWire[];
    models: readonly ModelWire[];
    /** The ground somebody has named, which is a thing to look for like any other. */
    places: readonly { id: number; name: string; width: number; height: number }[];
    shops: readonly { id: number; name: string }[];
  },
): Found[] {
  const said = wanted.trim().toLowerCase();
  if (said.length === 0) return [];

  const all: Found[] = [
    ...world.items.map((one) => ({
      kind: 'item' as const,
      id: String(one.id),
      name: one.name,
      note: one.kind,
    })),
    ...world.characters.map((one) => ({
      kind: 'character' as const,
      id: String(one.id),
      name: one.name,
      note: one.combatLevel > 0 ? `level ${one.combatLevel}` : 'talks',
    })),
    ...world.objects.map((one) => ({
      kind: 'scenery' as const,
      id: String(one.id),
      name: one.name,
      note: one.actions[0] ?? 'scenery',
    })),
    ...world.quests.map((one) => ({
      kind: 'quest' as const,
      id: one.id,
      name: one.name,
      note: `${one.steps.length} steps`,
    })),
    ...world.models.map((one) => ({
      kind: 'model' as const,
      id: one.name,
      name: one.title || one.name,
      note: one.tags.join(', ') || 'model',
    })),
    ...world.places.map((one) => ({
      kind: 'place' as const,
      id: String(one.id),
      name: one.name,
      note: `${one.width} by ${one.height}`,
    })),
    ...world.shops.map((one) => ({
      kind: 'shop' as const,
      id: String(one.id),
      name: one.name,
      note: 'shop',
    })),
  ];

  const rank = (one: Found): number => {
    const name = one.name.toLowerCase();
    if (one.id.toLowerCase() === said) return 0;
    if (name === said) return 1;
    if (name.startsWith(said)) return 2;
    if (name.includes(said)) return 3;
    return 4;
  };

  return all
    .map((one) => ({ one, at: rank(one) }))
    .filter((held) => held.at < 4)
    .sort((a, b) => a.at - b.at || a.one.name.localeCompare(b.one.name))
    .slice(0, 40)
    .map((held) => held.one);
}
